import {
  DEFAULT_DATA_UPLOAD_CONFIG,
  type DataUploadConfig,
  type DataUploadUseCaseKey,
  parseDataUploadConfig,
} from 'common/helpers/dataUpload'
import {isSelectiveAiDomainHost} from 'common/helpers/selectiveAiDomain'
import {useEffect, useState} from 'react'
import {useDispatch} from 'react-redux'
import {getDataUploadUseCases} from 'setup/redux/Actions/DataUpload/dataUpload'

/** One `data-upload/use-cases` response per page load: the limits do not change mid-session. */
let cachedResponse: unknown = null
let pendingListeners: Array<(response: unknown) => void> | null = null

/** Test seam: forget the cached `use-cases` response. */
export const resetDataUploadConfigCache = () => {
  cachedResponse = null
  pendingListeners = null
}

/**
 * Whether "any file -> our template" conversion exists on this server. The routes only exist
 * on the AI servers; elsewhere they answer 404 and every screen keeps its spreadsheet-only flow.
 */
export const isDataUploadConversionAvailable = (): boolean => isSelectiveAiDomainHost()

/**
 * Upload limits and accepted formats for a use case, read from `GET data-upload/use-cases`
 * (never hardcoded by the screens). Starts from the documented defaults, so a picker is usable
 * before the call answers and stays usable if it fails.
 *
 * @param useCaseKey Narrows the formats to what that use case accepts.
 * @param enabled Pass false where conversion is unavailable: no call is made then.
 */
export const useDataUploadConfig = (
  useCaseKey?: DataUploadUseCaseKey,
  enabled: boolean = isDataUploadConversionAvailable()
): DataUploadConfig => {
  const dispatch = useDispatch()
  const [response, setResponse] = useState<unknown>(cachedResponse)

  useEffect(() => {
    if (!enabled || cachedResponse) return
    let active = true
    const listener = (next: unknown) => {
      if (active) setResponse(next)
    }
    if (pendingListeners) {
      pendingListeners.push(listener)
    } else {
      pendingListeners = [listener]
      const settle = (next: unknown) => {
        cachedResponse = next
        const listeners = pendingListeners ?? []
        pendingListeners = null
        listeners.forEach((notify) => notify(next))
      }
      dispatch(getDataUploadUseCases(settle, () => settle(null)) as never)
    }
    return () => {
      active = false
    }
  }, [dispatch, enabled])

  return response ? parseDataUploadConfig(response, useCaseKey) : DEFAULT_DATA_UPLOAD_CONFIG
}
